import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  EmailTemplate,
  emailTemplatesService,
} from '@/services/emailTemplates.service';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Mail, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TemplateConfigSectionProps {
  folderUuid: string;
  currentTemplateConfigs?: {
    dsn?: string;
    bulletins?: string;
    editions?: string;
  };
  onConfigUpdate?: () => void;
}

export function TemplateConfigSection({
  folderUuid,
  currentTemplateConfigs = {},
  onConfigUpdate,
}: TemplateConfigSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const orgUuid = useOrganisationsStore(s => s.currentOrganisation?.uuid);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState({
    dsn: currentTemplateConfigs.dsn || '',
    bulletins: currentTemplateConfigs.bulletins || '',
    editions: currentTemplateConfigs.editions || '',
  });

  // Récupérer tous les templates disponibles
  const { data: allTemplates, isLoading } = useQuery({
    queryKey: ['email-templates-all', orgUuid],
    queryFn: () => emailTemplatesService.getAllTemplatesGrouped(),
  });

  // Récupérer la configuration actuelle des templates pour ce dossier
  const {
    data: folderTemplateConfig,
    isLoading: isLoadingConfig,
    error: configError,
  } = useQuery({
    queryKey: ['folder-template-config', folderUuid],
    queryFn: () => emailTemplatesService.getFolderTemplateConfig(folderUuid),
  });

  // Use fetched config or fallback to props
  const actualTemplateConfigs = folderTemplateConfig || currentTemplateConfigs;

  // Sync local state when configs change
  useEffect(() => {
    setSelectedTemplates({
      dsn: actualTemplateConfigs.dsn || '',
      bulletins: actualTemplateConfigs.bulletins || '',
      editions: actualTemplateConfigs.editions || '',
    });
  }, [actualTemplateConfigs]);

  // Mutation pour sauvegarder les configurations
  const saveConfigMutation = useMutation({
    mutationFn: async (configs: typeof selectedTemplates) => {
      return emailTemplatesService.saveFolderTemplateConfig(
        folderUuid,
        configs
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['folder-detail', folderUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['folder-template-config', folderUuid],
      });
      setIsModalOpen(false);
      onConfigUpdate?.();
    },
  });

  const handleSave = () => {
    saveConfigMutation.mutate(selectedTemplates);
  };

  const handleTemplateSelect = (
    type: 'dsn' | 'bulletins' | 'editions',
    templateUuid: string
  ) => {
    setSelectedTemplates(prev => ({
      ...prev,
      [type]: templateUuid,
    }));
  };

  const getSelectedTemplateName = (
    type: 'dsn' | 'bulletins' | 'editions',
    templateUuid?: string
  ): string => {
    if (!templateUuid || !allTemplates)
      return t('templates.noTemplateSelected', 'Aucun template sélectionné');

    const templates = allTemplates[type];
    const template = templates.find(t => t.uuid === templateUuid);
    return (
      template?.name || t('templates.templateNotFound', 'Template introuvable')
    );
  };

  const getSelectedTemplateSubject = (
    type: 'dsn' | 'bulletins' | 'editions',
    templateUuid?: string
  ): string => {
    if (!templateUuid || !allTemplates) return '';

    const templates = allTemplates[type];
    const template = templates.find(t => t.uuid === templateUuid);
    return (
      template?.subject ||
      t('templates.templateNotFound', 'Template introuvable')
    );
  };

  const templateTypeLabels = {
    dsn: t('templates.dsn', 'DSN'),
    bulletins: t('templates.bulletins', 'Bulletins'),
    editions: t('templates.editions', 'Éditions'),
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
          <FileText className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            {t('templateConfig.title', 'Templates Email')}
          </h2>
          <p className="text-sm text-neutral-500">
            {t(
              'templateConfig.description',
              'Configuration des templates par type de document'
            )}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Configuration actuelle */}
        <div className="space-y-3">
          {isLoadingConfig ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-lg bg-neutral-200"
                />
              ))}
            </div>
          ) : configError ? (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
              Erreur lors du chargement de la configuration:{' '}
              {configError.message}
            </div>
          ) : (
            Object.entries(templateTypeLabels).map(([type, label]) => (
              <div
                key={type}
                className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 p-4"
              >
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-neutral-500" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      {label}
                    </p>
                    <p className="text-xs font-medium text-neutral-900">
                      {getSelectedTemplateName(
                        type as 'dsn' | 'bulletins' | 'editions',
                        actualTemplateConfigs[
                          type as keyof typeof actualTemplateConfigs
                        ]
                      )}
                    </p>
                    {getSelectedTemplateSubject(
                      type as 'dsn' | 'bulletins' | 'editions',
                      actualTemplateConfigs[
                        type as keyof typeof actualTemplateConfigs
                      ]
                    ) && (
                      <p className="text-xs text-neutral-600">
                        {getSelectedTemplateSubject(
                          type as 'dsn' | 'bulletins' | 'editions',
                          actualTemplateConfigs[
                            type as keyof typeof actualTemplateConfigs
                          ]
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Bouton de configuration */}
        <Button
          onClick={() => setIsModalOpen(true)}
          variant="outline"
          className="w-full"
        >
          <Settings className="mr-2 h-4 w-4" />
          {t('templateConfig.configure', 'Configurer les templates')}
        </Button>
      </div>

      {/* Modal de configuration */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t(
          'templateConfig.configureTitle',
          'Configuration des templates'
        )}
        size="lg"
      >
        <div className="space-y-6">
          <p className="text-sm text-neutral-600">
            {t(
              'templateConfig.configureDescription',
              'Sélectionnez les templates à utiliser pour chaque type de document pour ce dossier.'
            )}
          </p>

          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-20 animate-pulse rounded-lg bg-neutral-200"
                />
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(templateTypeLabels).map(([type, label]) => (
                <div key={type} className="space-y-3">
                  <h3 className="text-sm font-medium text-neutral-900">
                    {t('templateConfig.selectFor', 'Template pour {{type}}', {
                      type: label,
                    })}
                  </h3>

                  <div className="space-y-2">
                    {/* Option par défaut */}
                    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50">
                      <input
                        type="radio"
                        name={`template-${type}`}
                        value=""
                        checked={
                          selectedTemplates[
                            type as keyof typeof selectedTemplates
                          ] === ''
                        }
                        onChange={() =>
                          handleTemplateSelect(
                            type as 'dsn' | 'bulletins' | 'editions',
                            ''
                          )
                        }
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t(
                            'templateConfig.useDefault',
                            'Utiliser le template par défaut'
                          )}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {t(
                            'templateConfig.useDefaultDescription',
                            'Template système par défaut'
                          )}
                        </p>
                      </div>
                    </label>

                    {/* Templates disponibles */}
                    {allTemplates?.[type as keyof typeof allTemplates]?.map(
                      (template: EmailTemplate) => (
                        <label
                          key={template.uuid}
                          className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50"
                        >
                          <input
                            type="radio"
                            name={`template-${type}`}
                            value={template.uuid}
                            checked={
                              selectedTemplates[
                                type as keyof typeof selectedTemplates
                              ] === template.uuid
                            }
                            onChange={() =>
                              handleTemplateSelect(
                                type as 'dsn' | 'bulletins' | 'editions',
                                template.uuid
                              )
                            }
                            className="text-primary-600 focus:ring-primary-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-neutral-900">
                                {template.name}
                              </p>
                              {template.isDefault && (
                                <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700">
                                  {t('templates.default', 'Défaut')}
                                </span>
                              )}
                              {!template.isActive && (
                                <span className="rounded-full bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600">
                                  {t('common.inactive', 'Inactif')}
                                </span>
                              )}
                            </div>
                            <p className="truncate text-xs text-neutral-500">
                              {template.subject}
                            </p>
                          </div>
                        </label>
                      )
                    )}

                    {(!allTemplates?.[type as keyof typeof allTemplates] ||
                      allTemplates[type as keyof typeof allTemplates].length ===
                        0) && (
                      <p className="text-sm text-neutral-500 italic">
                        {t(
                          'templateConfig.noTemplatesAvailable',
                          'Aucun template disponible pour ce type'
                        )}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              disabled={saveConfigMutation.isPending}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveConfigMutation.isPending}
            >
              {saveConfigMutation.isPending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {t('common.saving', 'Sauvegarde...')}
                </>
              ) : (
                t('common.save', 'Sauvegarder')
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
